import {
    formatTimecode,
    formatSpeakers,
    formatTags,
    formatPlainText,
    formatYesNo,
    formatApproved,
    wrapValue,
    normalizeLineBreaks,
} from './formatters.js';
import { isFieldEnabled, PROSE_FIELD_IDS, LABELED_FIELD_IDS, normalizeBreakAfter } from './options.js';

/**
 * @typedef {Object} TranscriptRun
 * @property {string} fieldId
 * @property {string} text May contain "\n" when line breaks are preserved.
 * @property {boolean} bold
 * @property {boolean} italic
 * @property {import('./options.js').TranscriptBreakAfter} breakAfter
 */

/**
 * @param {import('./options.js').TranscriptFieldConfig[]} fieldLayout
 * @param {Set<string>} [allowedIds]
 * @returns {import('./options.js').TranscriptFieldConfig[]}
 */
function activeFields(fieldLayout, allowedIds) {
    return (fieldLayout || []).filter((field) => {
        if (!field.enabled) return false;
        if (allowedIds && !allowedIds.has(field.id)) return false;
        return true;
    });
}

/**
 * Resolve the raw value (no label, no wrap) for a field.
 * @returns {string | null} null means skip (empty optional)
 */
function resolveFieldValue(fieldId, ctx, labels) {
    const { event, index, start, end, project, options, eventGroupType } = ctx;
    const preserve = Boolean(options.preserveLineBreaks);
    const lineBreak = preserve ? '\n' : ' ';

    switch (fieldId) {
        case 'index':
            return index != null ? String(index) : null;
        case 'start':
            return formatTimecode(start, options.tcFormat, project);
        case 'end':
            return formatTimecode(end, options.tcFormat, project);
        case 'speakers':
            return formatSpeakers(event?.speakers || [], ', ') || null;
        case 'text':
            return formatPlainText(event?.text, lineBreak) || null;
        case 'original':
            if (eventGroupType !== 'translation') return null;
            return formatPlainText(event?.original, lineBreak) || null;
        case 'tags':
            return formatTags(event?.tags || []) || null;
        case 'notes':
            return event?.notes ? normalizeLineBreaks(event.notes, preserve) || null : null;
        case 'notesCheck':
            return formatYesNo(Boolean(event?.notesCheck), labels);
        case 'reply':
            return event?.reply ? normalizeLineBreaks(event.reply, preserve) || null : null;
        case 'replyCheck':
            return formatYesNo(Boolean(event?.replyCheck), labels);
        case 'approved':
            return formatApproved(event?.approved, labels);
        case 'forced':
            return formatYesNo(Boolean(event?.forced), labels);
        default:
            return null;
    }
}

/** @param {string} fieldId @param {import('./formatters.js').TranscriptLabelSet} labels */
function fieldPrefixLabel(fieldId, labels) {
    if (!LABELED_FIELD_IDS.has(fieldId)) return null;
    return fieldId === 'approved' ? labels.approvedLabel : labels[fieldId];
}

/**
 * Resolve display text for a field: value, wrapped, then optionally prefixed with its label.
 * @param {import('./options.js').TranscriptFieldConfig} field
 * @returns {string | null} null means skip (empty optional)
 */
function resolveFieldText(field, ctx, labels) {
    const value = resolveFieldValue(field.id, ctx, labels);
    if (value == null || value === '') return null;

    const wrap = field.wrap || 'none';
    if (field.id === 'index' && wrap === 'none') return `${value})`;

    const wrapped = wrapValue(value, wrap);
    const label = field.showLabel !== false ? fieldPrefixLabel(field.id, labels) : null;
    return label ? `${label}: ${wrapped}` : wrapped;
}

/**
 * Build ordered runs for a single cue/event.
 *
 * @param {object} params
 * @param {object} params.event
 * @param {number} [params.index] 1-based index
 * @param {import('./options.js').TranscriptExportOptions} params.options
 * @param {{ frameRate: number|string, dropFrame: boolean }} params.project
 * @param {import('./formatters.js').TranscriptLabelSet} params.labels
 * @param {string} [params.eventGroupType]
 * @param {Set<string>} [params.allowedIds]
 * @param {number} [params.start]
 * @param {number} [params.end]
 * @returns {TranscriptRun[]}
 */
export function buildCueRuns({
    event,
    index,
    options,
    project,
    labels,
    eventGroupType,
    allowedIds,
    start = event?.start,
    end = event?.end,
}) {
    const fields = activeFields(options.fieldLayout, allowedIds);
    /** @type {TranscriptRun[]} */
    const runs = [];

    const ctx = { event, index, start, end, project, options, eventGroupType };

    for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        let text = resolveFieldText(field, ctx, labels);
        if (text == null || text === '') continue;

        const breakAfter = normalizeBreakAfter(field.breakAfter);

        // Speakers followed inline by text → "Name: "
        if (field.id === 'speakers') {
            const nextEnabled = fields.slice(i + 1).find((f) => {
                const t = resolveFieldText(f, ctx, labels);
                return t != null && t !== '';
            });
            if (nextEnabled?.id === 'text' && breakAfter === 0) {
                text = `${text}: `;
            }
        }

        runs.push({
            fieldId: field.id,
            text,
            bold: Boolean(field.bold),
            italic: Boolean(field.italic),
            breakAfter,
        });
    }

    return runs;
}

/**
 * Build runs for a prose group (index/start/end once; speakers+text joined as body).
 *
 * @param {object} params
 * @param {object[]} params.events
 * @param {number} [params.index]
 * @param {import('./options.js').TranscriptExportOptions} params.options
 * @param {{ frameRate: number|string, dropFrame: boolean }} params.project
 * @param {import('./formatters.js').TranscriptLabelSet} params.labels
 * @returns {TranscriptRun[]}
 */
export function buildProseGroupRuns({ events, index, options, project, labels }) {
    const fields = activeFields(options.fieldLayout, PROSE_FIELD_IDS);
    /** @type {TranscriptRun[]} */
    const runs = [];

    const start = events[0].start;
    const end = events[events.length - 1].end;
    const includeSpeakers = isFieldEnabled(options.fieldLayout, 'speakers');

    for (const field of fields) {
        if (field.id === 'speakers' || field.id === 'text') {
            continue; // handled as combined body below
        }

        const text = resolveFieldText(
            field,
            { event: events[0], index, start, end, project, options },
            labels
        );
        if (text == null || text === '') continue;

        runs.push({
            fieldId: field.id,
            text,
            bold: Boolean(field.bold),
            italic: Boolean(field.italic),
            breakAfter: normalizeBreakAfter(field.breakAfter),
        });
    }

    // Body: speaker/text segments joined with spaces
    if (isFieldEnabled(options.fieldLayout, 'text')) {
        const textField = options.fieldLayout.find((f) => f.id === 'text');
        const speakerField = options.fieldLayout.find((f) => f.id === 'speakers');
        const lineBreak = options.preserveLineBreaks ? '\n' : ' ';
        const bodyParts = events.map((event) => {
            const plain = formatPlainText(event.text, lineBreak);
            if (!plain) return '';
            const text = wrapValue(plain, textField?.wrap);
            if (!includeSpeakers) return text;
            const speaker = formatSpeakers(event.speakers || [], ', ');
            return speaker ? `${wrapValue(speaker, speakerField?.wrap)}: ${text}` : text;
        });
        const body = bodyParts.filter(Boolean).join(' ');
        if (body) {
            // Emit as a single text run; speaker bold is approximate for prose (whole body uses text styles)
            runs.push({
                fieldId: 'text',
                text: body,
                bold: Boolean(textField?.bold || speakerField?.bold),
                italic: Boolean(textField?.italic),
                breakAfter: textField ? normalizeBreakAfter(textField.breakAfter) : 1,
            });
        }
    }

    return runs;
}

/**
 * Serialize runs to plain text (ignores bold/italic).
 * @param {TranscriptRun[]} runs
 * @returns {string}
 */
export function runsToPlainText(runs) {
    let line = '';
    let out = '';

    const flush = (breaks = 1) => {
        if (line.length) {
            out += line + '\n'.repeat(Math.max(1, breaks));
            line = '';
        }
    };

    for (let i = 0; i < runs.length; i++) {
        const run = runs[i];
        if (line.length) {
            // Avoid double separators when speaker already ends with ": "
            line += /\s$/.test(line) ? '' : '\t';
        }
        line += run.text;
        if (run.breakAfter) flush(run.breakAfter);
    }
    flush();
    return out;
}

/**
 * Group runs into lines for DOCX paragraphs. A line ends with a blank line
 * when its last run has `breakAfter === 2`.
 * @param {TranscriptRun[]} runs
 * @returns {TranscriptRun[][]}
 */
export function groupRunsIntoLines(runs) {
    /** @type {TranscriptRun[][]} */
    const lines = [];
    /** @type {TranscriptRun[]} */
    let current = [];

    for (const run of runs) {
        current.push(run);
        if (run.breakAfter) {
            lines.push(current);
            current = [];
        }
    }
    if (current.length) lines.push(current);
    return lines;
}
